import { EColorPermissionFloorPlan } from "@/common/entity/Enum";
import { EAlignment, EPosition } from "@/components/ui/TreeSelect/Models";
import { ResourceInfo } from "@/services";
import { ETypeFloorPlan } from "@/services/resource-service/entity/Enums";
import {
  Popover,
  PopoverSurface,
  PositioningImperativeRef,
  PositioningVirtualElement,
  // Tooltip,
} from "@fluentui/react-components";
import { createVirtualElementFromClick } from "@fluentui/react-positioning";
import { fabric } from "fabric";
import {
  ICircleOptions,
  IPolylineOptions,
  IRectOptions,
  ITextOptions,
  ITriangleOptions,
} from "fabric/fabric-impl";
import { useEffect, useMemo, useRef, useState } from "react";
import { IDrawFloorPlanProps } from "./Models";
import {
  DrawFloorPlanBaseWrapper,
  LayerZoomWrapper,
  // HeaderActionWrapper,
  ResourceDetailFloorPlanEmailWrapper,
  ResourceDetailFloorPlanLocationWrapper,
  ResourceDetailFloorPlanNameWrapper,
  ResourceDetailFloorPlanWrapper,
  TextWrapper,
} from "./Styles";
import { IResourceStore } from "@/redux/slices/resources/ResourceReducerModel";
import { useSelector } from "react-redux";
import { RootState } from "@/redux";
import {
  // RenderIconDismissSmall,
  ResourceDetailEmailIcon,
  ResourceDetailLocationIcon,
  ResourceDetailNameIcon,
} from "@/components/ui/Icons/IconsCustom";
import IconPermission from "@/view/addin/components/icon-permission";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import TentativePermission from "@/view/addin/components/icon-permission/TentativeIcon";
import { FloorPlanEvents } from "../FloorPlanEvents";

const DrawFloorPlan = (props: IDrawFloorPlanProps) => {
  const resourceStore: IResourceStore = useSelector(
    (state: RootState) => state.resources
  ) as IResourceStore;
  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;
  const theme: string = useSelector(
    (state: RootState) => state.settingInfo?.theme
  ) as string;

  const [open, setOpen] = useState<boolean>(false);
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const [resourceInfo, setResourceInfo] = useState<ResourceInfo | null>(null);
  const [divSize, setDivSize] = useState({ width: 0, height: 0 });
  const state_ref = useRef(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [isShowLayer, setIsShowLayer] = useState<boolean>(false);

  useEffect(() => {
    const handleClick = () => {
      setIsShowLayer(false);
    };

    document.addEventListener("keydown", handleClick);

    return () => {
      document.removeEventListener("keydown", handleClick);
    };
  }, []);

  useEffect(() => {
    if (divSize.height && divSize.width && state_ref?.current && !canvas) {
      const element: HTMLElement = state_ref?.current;
      if (element) {
        drawCanvas();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.floorPlanInfo), divSize, props.maxHeight]);

  useEffect(() => {
    if (canvas) {
      getMeta(props.floorPlanInfo?.path, (err, img) => {
        fabric.Image.fromURL(props.floorPlanInfo?.path, async (image) => {
          image.set({
            hasControls: false,
            selectable: false,
          });
          canvas?.add(image);
          drawResource(canvas);
          zoomCanvas(canvas, image);
          canvas.off("mouse:wheel");
          canvas.on("mouse:wheel", (opt) => {
            if (opt.e.ctrlKey) {
              if (isShowLayer) {
                setIsShowLayer(false);
              }
              const delta = opt.e.deltaY;
              let zoomRatio = canvas.getZoom();
              zoomRatio *= 0.999 ** delta;
              if (zoomRatio > 5) zoomRatio = 5;
              if (zoomRatio < 0.2) zoomRatio = 0.2;
              canvas.zoomToPoint(
                { x: opt.e.offsetX, y: opt.e.offsetY },
                zoomRatio
              );
              opt.e.preventDefault();
              opt.e.stopPropagation();
            } else {
              setIsShowLayer(true);
            }
          });
          canvas.requestRenderAll();
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divSize]);

  useEffect(() => {
    reDrawWhenChangeSelectedResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(resourceStore.selectedResource)]);

  const zoomCanvas = (
    canvasClone: fabric.Canvas,
    image: fabric.Image
  ): void => {
    let delta = 1;
    let deltaWidth = 1;
    let deltaHeight = 1;
    if (
      canvasClone &&
      image.width &&
      image.height &&
      divSize.width &&
      divSize.height
    ) {
      const heightClone = props.maxHeight ? props.maxHeight : divSize.height;
      if (image.width > divSize.width) {
        deltaWidth = divSize.width / image.width;
      }
      if (image.height > heightClone) {
        deltaHeight = heightClone / image.height;
      }

      delta = deltaWidth < deltaHeight ? deltaWidth : deltaHeight;
      if (delta === 1) {
        if (image.width < divSize.width) {
          deltaWidth = divSize.width / image.width;
        }
        if (image.height < heightClone) {
          deltaHeight = heightClone / image.height;
        }
        delta = deltaWidth < deltaHeight ? deltaWidth : deltaHeight;
      }
      if (delta !== 1) {
        canvasClone.zoomToPoint(
          {
            x: 0,
            y: 0,
          },
          delta
        );
        canvasClone.setDimensions({
          width: (image?.width || window.innerWidth) * delta,
          height: (image?.height || window.innerWidth) * delta,
        });
      }
    }
  };

  const removeEventElementCanvas = (element: fabric.Object) => {
    element.off("mousedown");
    element.off("mouseout");
    element.off("mouseover");
  };

  const reDrawWhenChangeSelectedResource = () => {
    if (canvas) {
      const objectCanvas: fabric.Object[] = canvas.getObjects();
      console.log("objectCanvas", objectCanvas);

      const resourceIds: string[] = (resourceStore.selectedResource || [])?.map(
        (item: ResourceInfo) => item.guid
      );
      objectCanvas.forEach((element) => {
        if (
          element.data?.guid &&
          resourceIds.length &&
          resourceIds.includes(element.data?.guid)
        ) {
          element.set("fill", EColorPermissionFloorPlan.Selected);
          const resourceInfoClone: ResourceInfo = element.data as ResourceInfo;
          if (element && resourceInfoClone) {
            removeEventElementCanvas(element);
            element.on("mousedown", (options) => {
              options.e.preventDefault();
              deleteSelectResource(resourceInfoClone);
            });
            element.on("mouseover", (e) => {
              setResourceInfo(resourceInfoClone);
              (element as fabric.Rect).set(
                "fill",
                EColorPermissionFloorPlan.Hover
              );
              canvas.requestRenderAll();
              toggleCallout(true, e.e);
              FloorPlanEvents.ElementMouseOver.Fire();
            });
            element.on("mouseout", () => {
              (element as fabric.Rect).set(
                "fill",
                EColorPermissionFloorPlan.Selected
              );
              canvas.requestRenderAll();
              // toggleCallout(false, null);
            });
          }
        } else if (element.data?.guid) {
          const resourceInfoClone: ResourceInfo = element.data as ResourceInfo;
          const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
            ? resourceInfoClone?.permission?.isAcceptSelectRecurring(
                orgStore.outlookInfo?.occurrences?.length || 0
              )
            : resourceInfoClone?.permission?.isAcceptSelect;
          const colorPermission = orgStore.outlookInfo?.isRecurringMaster
            ? resourceInfoClone?.permission?.colorPermissionRecurring(
                orgStore.outlookInfo?.occurrences?.length || 0
              )
            : resourceInfoClone?.permission?.colorPermission;
          element.set("fill", colorPermission);
          element.set(
            "hoverCursor",
            isAcceptSelect ? "pointer" : "not-allowed"
          );
          if (element && resourceInfoClone) {
            removeEventElementCanvas(element);
            if (isAcceptSelect) {
              element.on("mousedown", (options) => {
                options.e.preventDefault();
                onSelect(resourceInfoClone);
              });
            }
            element.on("mouseover", (e) => {
              setResourceInfo(resourceInfoClone);
              (element as fabric.Rect).set(
                "fill",
                EColorPermissionFloorPlan.Hover
              );
              canvas.requestRenderAll();
              toggleCallout(true, e.e);
              FloorPlanEvents.ElementMouseOver.Fire();
            });
            element.on("mouseout", () => {
              (element as fabric.Rect).set("fill", colorPermission);
              canvas.requestRenderAll();
              // toggleCallout(false, null);
            });
          }
        }
      });
      canvas?.requestRenderAll();
    }
  };

  const handleResize = () => {
    if (state_ref.current) {
      const element: HTMLElement = state_ref.current;
      const newDivWidth = element?.clientWidth;
      const newDivHeight = element?.clientHeight;
      setDivSize({ width: newDivWidth, height: newDivHeight });
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getMeta = (url: string, callback: (err: any, img?: any) => void) => {
    const img = new Image();
    img.onload = () => callback(null, img);
    img.onerror = (err) => callback(err);
    img.src = url;
  };

  const drawCanvas = (): void => {
    getMeta(props.floorPlanInfo?.path, (err, img) => {
      const newCanvas: fabric.Canvas = initCanvas();
      fabric.Image.fromURL(props.floorPlanInfo?.path, async (image) => {
        image.set({
          hasControls: false,
          selectable: false,
        });
        newCanvas?.add(image);
        drawResource(newCanvas);
        zoomCanvas(newCanvas, image);
        newCanvas.off("mouse:wheel");
        newCanvas.on("mouse:wheel", (opt) => {
          if (opt.e.ctrlKey) {
            if (isShowLayer) {
              setIsShowLayer(false);
            }
            const delta = opt.e.deltaY;
            let zoomRatio = newCanvas.getZoom();
            zoomRatio *= 0.999 ** delta;
            if (zoomRatio > 5) zoomRatio = 5;
            if (zoomRatio < 0.2) zoomRatio = 0.2;
            newCanvas.zoomToPoint(
              { x: opt.e.offsetX, y: opt.e.offsetY },
              zoomRatio
            );
            opt.e.preventDefault();
            opt.e.stopPropagation();
          } else {
            setIsShowLayer(true);
          }
        });
        newCanvas.requestRenderAll();
      });
    });
  };

  const toggleCallout = (show: boolean, event: MouseEvent | null) => {
    if (show) {
      setOpen(true);
      const target: PositioningVirtualElement | null = event
        ? createVirtualElementFromClick(event)
        : null;
      positioningRef.current?.setTarget(target);
    } else {
      setOpen(false);
    }
  };

  // const onCloseTooltip = () => {
  //   setOpen(false);
  //   setResourceInfo(null);
  // };

  const drawResource = (newCanvas: fabric.Canvas): void => {
    const resourceIds: string[] = (resourceStore.selectedResource || [])?.map(
      (item: ResourceInfo) => item.guid
    );
    (props.floorPlanInfo?.externalResources || []).forEach(
      (item: ResourceInfo) => {
        const isSelected: boolean =
          !!resourceIds.length && resourceIds.includes(item.guid);
        item.resourceFloorPlanInfo?.forEach((rsFloorPlan) => {
          if (rsFloorPlan?.type === ETypeFloorPlan.Rect) {
            const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.isAcceptSelectRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.isAcceptSelect;
            const colorPermission = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.colorPermissionRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.colorPermission;
            const element = new fabric.Rect({
              ...(rsFloorPlan as IRectOptions),
              hasControls: false,
              selectable: false,
              fill: isSelected
                ? EColorPermissionFloorPlan.Selected
                : colorPermission,
              lockScalingX: true,
              lockScalingY: true,
              hoverCursor: isAcceptSelect ? "pointer" : "not-allowed",
            });
            if (element) {
              removeEventElementCanvas(element);
              addEventElement(element, newCanvas, item, isSelected);
            }
          } else if (rsFloorPlan?.type === ETypeFloorPlan.Circle) {
            const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.isAcceptSelectRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.isAcceptSelect;
            const colorPermission = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.colorPermissionRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.colorPermission;
            const element = new fabric.Circle({
              ...(rsFloorPlan as ICircleOptions),
              hasControls: false,
              selectable: false,
              fill: isSelected
                ? EColorPermissionFloorPlan.Selected
                : colorPermission,
              lockScalingX: true,
              lockScalingY: true,
              hoverCursor: isAcceptSelect ? "pointer" : "not-allowed",
            });
            if (element) {
              removeEventElementCanvas(element);
              addEventElement(element, newCanvas, item, isSelected);
            }
          } else if (rsFloorPlan?.type === ETypeFloorPlan.Polygon) {
            const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.isAcceptSelectRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.isAcceptSelect;
            const colorPermission = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.colorPermissionRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.colorPermission;
            const element = new fabric.Polygon(
              (rsFloorPlan as IPolylineOptions).points || [],
              {
                ...(rsFloorPlan as IPolylineOptions),
                hasControls: false,
                selectable: false,
                fill: isSelected
                  ? EColorPermissionFloorPlan.Selected
                  : colorPermission,
                lockScalingX: true,
                lockScalingY: true,
                hoverCursor: isAcceptSelect ? "pointer" : "not-allowed",
              }
            );
            if (element) {
              removeEventElementCanvas(element);
              addEventElement(element, newCanvas, item, isSelected);
            }
          } else if (rsFloorPlan?.type === ETypeFloorPlan.Triangle) {
            const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.isAcceptSelectRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.isAcceptSelect;
            const colorPermission = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.colorPermissionRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.colorPermission;
            const element = new fabric.Triangle({
              ...(rsFloorPlan as ITriangleOptions),
              hasControls: false,
              selectable: false,
              fill: isSelected
                ? EColorPermissionFloorPlan.Selected
                : colorPermission,
              lockScalingX: true,
              lockScalingY: true,
              hoverCursor: isAcceptSelect ? "pointer" : "not-allowed",
            });
            if (element) {
              removeEventElementCanvas(element);
              addEventElement(element, newCanvas, item, isSelected);
            }
          } else if (rsFloorPlan?.type === ETypeFloorPlan.Itext) {
            const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.isAcceptSelectRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.isAcceptSelect;
            const colorPermission = orgStore.outlookInfo?.isRecurringMaster
              ? item?.permission?.colorPermissionRecurring(
                  orgStore.outlookInfo?.occurrences?.length || 0
                )
              : item?.permission?.colorPermission;
            const element = new fabric.IText(
              (rsFloorPlan as ITextOptions).text || "",
              {
                ...(rsFloorPlan as ITextOptions),
                hasControls: false,
                selectable: false,
                fill: isSelected
                  ? EColorPermissionFloorPlan.Selected
                  : colorPermission,
                lockScalingX: true,
                lockScalingY: true,
                hoverCursor: isAcceptSelect ? "pointer" : "not-allowed",
              }
            );
            if (element) {
              removeEventElementCanvas(element);
              addEventElement(element, newCanvas, item, isSelected);
            }
          }
        });
      }
    );
    if (!canvas) {
      setCanvas(newCanvas);
    }
  };

  const addEventElement = (
    element:
      | fabric.Rect
      | fabric.Circle
      | fabric.Polygon
      | fabric.Triangle
      | fabric.IText,
    canvasClone: fabric.Canvas,
    resourceInfoClone: ResourceInfo,
    isSelected?: boolean
  ) => {
    const isAcceptSelect = orgStore.outlookInfo?.isRecurringMaster
      ? resourceInfoClone?.permission?.isAcceptSelectRecurring(
          orgStore.outlookInfo?.occurrences?.length || 0
        )
      : resourceInfoClone?.permission?.isAcceptSelect;
    if (isAcceptSelect) {
      element.on("mousedown", (options) => {
        options.e.preventDefault();
        isSelected
          ? deleteSelectResource(resourceInfoClone)
          : onSelect(resourceInfoClone);
      });
    }
    element.on("mouseover", (e) => {
      setResourceInfo(resourceInfoClone);
      (element as fabric.Rect).set("fill", EColorPermissionFloorPlan.Hover);
      canvasClone.requestRenderAll();
      toggleCallout(true, e.e);
      FloorPlanEvents.ElementMouseOver.Fire(); // Fire the event when mouseover occurs
    });
    element.on("mouseout", () => {
      const colorPermission = orgStore.outlookInfo?.isRecurringMaster
        ? resourceInfoClone?.permission?.colorPermissionRecurring(
            orgStore.outlookInfo?.occurrences?.length || 0
          )
        : resourceInfoClone?.permission?.colorPermission;
      (element as fabric.Rect).set(
        "fill",
        isSelected ? EColorPermissionFloorPlan.Selected : colorPermission
      );
      canvasClone.requestRenderAll();
      // toggleCallout(false, null);
    });
    canvasClone.add(element);
  };

  const initCanvas = (): fabric.Canvas => {
    const newCanvas: fabric.Canvas = new fabric.Canvas("canvas", {
      preserveObjectStacking: false,
      stopContextMenu: true,
      fireRightClick: true,
    });
    // newCanvas.setDimensions({
    //   width: width ?? window.innerWidth * 0.6,
    //   height: height ?? window.innerHeight * 0.7,
    // });
    return newCanvas;
  };

  const renderResourceDetail = (): JSX.Element | string => {
    const resourceIds: string[] = (resourceStore.selectedResource || [])?.map(
      (item: ResourceInfo) => item.email.toLowerCase()
    );
    const isSelected: boolean =
      !!resourceIds.length &&
      resourceIds.includes(
        resourceInfo?.email ? resourceInfo?.email?.toLowerCase() : ""
      );
    const colorDetail = isSelected
      ? EColorPermissionFloorPlan.Selected
      : orgStore.outlookInfo?.isRecurringMaster
      ? resourceInfo?.permission?.colorPermissionRecurring(
          orgStore.outlookInfo?.occurrences?.length || 0
        )
      : resourceInfo?.permission?.colorPermission;
    return resourceInfo ? (
      <ResourceDetailFloorPlanWrapper
        className="ResourceDetailFloorPlanWrapper"
        $colorBorder={colorDetail}
      >
        {/* <HeaderActionWrapper className="HeaderActionWrapper"><div className="icon-action" onClick={onCloseTooltip}><RenderIconDismissSmall theme={theme} /></div></HeaderActionWrapper> */}
        <ResourceDetailFloorPlanNameWrapper className="ResourceDetailFloorPlanNameWrapper">
          <ResourceDetailNameIcon color={colorDetail} />
          <div className={"w-100 d-flex"}>
            <TextWrapper className={"TextWrapper"}>
              {resourceInfo.displayName}
            </TextWrapper>
            {resourceInfo.permission.meetingDeclines?.length ? (
              <IconPermission
                permission={resourceInfo.permission}
                exchangeProperty={resourceInfo.exchangeProperty}
                isRecurring={orgStore.outlookInfo?.isRecurringMaster}
                quantityMeetingInfo={
                  orgStore.outlookInfo?.occurrences?.length || 0
                }
              />
            ) : null}
            {resourceInfo.permission.isTentative ? (
              <TentativePermission
                permission={resourceInfo.permission}
                theme={theme}
              />
            ) : null}
          </div>
        </ResourceDetailFloorPlanNameWrapper>
        <ResourceDetailFloorPlanLocationWrapper className="ResourceDetailFloorPlanLocationWrapper">
          <ResourceDetailLocationIcon color={colorDetail} />
          <TextWrapper className={"TextWrapper w-100"}>
            {resourceInfo.locationDisplayName}
          </TextWrapper>
        </ResourceDetailFloorPlanLocationWrapper>
        <ResourceDetailFloorPlanEmailWrapper className="ResourceDetailFloorPlanEmailWrapper">
          <ResourceDetailEmailIcon color={colorDetail} />
          <TextWrapper className={"TextWrapper w-100"}>
            {resourceInfo.displayEmail || resourceInfo.email}
          </TextWrapper>
        </ResourceDetailFloorPlanEmailWrapper>
      </ResourceDetailFloorPlanWrapper>
    ) : (
      ""
    );
  };

  const renderTooltip = () => {
    return (
      <Popover
        withArrow
        onOpenChange={(_, data) => setOpen(data.open)}
        positioning={{
          positioningRef,
          position: EPosition.BELOW,
          align: EAlignment.START,
        }}
        open={open}
        trapFocus
      >
        <PopoverSurface className="custom-popover">
          <>{renderResourceDetail()}</>
        </PopoverSurface>
      </Popover>
    );
  };

  const renderCanvas = useMemo(() => {
    return <canvas id="canvas"></canvas>;
  }, []);

  const onSelect = (resourceInfo: ResourceInfo) => {
    const selectedRsString: string[] = (
      resourceStore.selectedResource || []
    )?.map((item) => item.email);
    if (
      props.selectResource &&
      !selectedRsString.includes(resourceInfo.email.toLowerCase())
    ) {
      props.selectResource(resourceInfo);
    }
  };

  const deleteSelectResource = (resourceInfo: ResourceInfo) => {
    const selectedRsString: string[] = (
      resourceStore.selectedResource || []
    )?.map((item) => item.email);
    if (
      props.deleteSelectResource &&
      selectedRsString.includes(resourceInfo.email.toLowerCase())
    ) {
      props.deleteSelectResource(resourceInfo);
    }
  };

  return (
    <DrawFloorPlanBaseWrapper
      className="DrawFloorPlanBaseWrapper"
      ref={state_ref}
    >
      {renderCanvas}
      {renderTooltip()}
      {isShowLayer ? (
        <LayerZoomWrapper
          onClick={() => setIsShowLayer(false)}
          onMouseLeave={() => setIsShowLayer(false)}
          className="ms-Overlay"
        >
          <b>
            Hold down the <code className="ctrl-key">Ctrl</code> key
          </b>
          &nbsp;while scrolling the mouse wheel to zoom in and out
        </LayerZoomWrapper>
      ) : null}
    </DrawFloorPlanBaseWrapper>
  );
};

export default DrawFloorPlan;
